import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  LockIcon,
  Menu,
} from "lucide-react";
import { SetupChecklistItem } from "@/types/checklist";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MenuBar from "@/components/MenuBar";

interface CheckListProps {
  items: SetupChecklistItem[];
  totalCompleted: number;
  totalItems: number;
}

const CheckList: React.FC<CheckListProps> = ({
  items,
  totalCompleted,
  totalItems,
}) => {
  const navigate = useNavigate();
  const progress = (totalCompleted / totalItems) * 100;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Find prerequisite items
  const parentInfoItem = items.find(
    (item) => item.name === "Parent Information",
  );
  const employerManagementItem = items.find(
    (item) => item.name === "Employer Management",
  );
  const employeeManagementItem = items.find(
    (item) => item.name === "Employee Management",
  );

  const parentAndEmployerComplete =
    parentInfoItem?.completed && employerManagementItem?.completed;

  const isItemDisabled = (itemName: string): boolean => {
    if (
      !parentAndEmployerComplete &&
      itemName !== "Parent Information" &&
      itemName !== "Employer Management"
    ) {
      return true;
    }

    if (
      !employeeManagementItem?.completed &&
      (itemName === "Pensions" || itemName === "Earnings")
    ) {
      return true;
    }

    return false;
  };

  const getTooltipMessage = (itemName: string): string => {
    if (!parentAndEmployerComplete) {
      return "Complete Parent Information and Employer Management first";
    }
    if (
      !employeeManagementItem?.completed &&
      (itemName === "Pensions" || itemName === "Earnings")
    ) {
      return "Complete Employee Management first";
    }
    return "";
  };

  const handleItemClick = (item: SetupChecklistItem) => {
    if (!isItemDisabled(item.name)) {
      if (item.name === "Parent Information") {
        navigate(`${item.path}?from=checklist`);
      } else {
        navigate(item.path);
      }
    }
  };

  const renderItem = (item: SetupChecklistItem, isSubItem: boolean = false) => {
    const isDisabled = isItemDisabled(item.name);
    const tooltipMessage = getTooltipMessage(item.name);

    const itemContent = (
      <div
        className={cn(
          "flex items-center justify-between",
          isSubItem ? "p-3 bg-gray-50" : "p-4 bg-white",
          "rounded-lg border",
          isDisabled
            ? "opacity-60 cursor-not-allowed"
            : "cursor-pointer hover:bg-gray-50",
        )}
        onClick={() => !isDisabled && handleItemClick(item)}
      >
        <div className="flex items-center space-x-3">
          {item.completed ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : isDisabled ? (
            <LockIcon className="h-5 w-5 text-gray-300" />
          ) : (
            <Circle className="h-5 w-5 text-gray-300" />
          )}
          <div>
            <h3
              className={cn("font-medium", isSubItem ? "text-sm" : "text-base")}
            >
              {item.name}
            </h3>
            <p
              className={cn(
                "text-gray-500",
                isSubItem ? "text-xs" : "text-sm",
                "hidden sm:block", // Hide description on mobile
              )}
            >
              {item.description}
            </p>
          </div>
        </div>
        <ChevronRight
          className={cn("h-5 w-5 text-gray-400", isSubItem && "h-4 w-4")}
        />
      </div>
    );

    return isDisabled ? (
      <TooltipProvider key={item.name}>
        <Tooltip>
          <TooltipTrigger asChild>{itemContent}</TooltipTrigger>
          <TooltipContent>
            <p>{tooltipMessage}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      itemContent
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1  mt-16 md:mt-0">
        <div className="p-4 md:p-6">
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="text-xl font-bold">
                Setup Checklist
              </CardTitle>
              <CardDescription className="hidden sm:block">
                Complete these items to start running payroll. You must complete
                these in the prescribed order as information is dependent on one
                another.
              </CardDescription>
              <div className="mt-2">
                <Progress value={progress} className="h-2" />
                <p className="text-sm text-gray-500 mt-1">
                  {totalCompleted} of {totalItems} items completed
                </p>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {items
                  .sort((a, b) => a.order - b.order)
                  .map((item) => (
                    <div key={item.name} className="space-y-2">
                      {renderItem(item)}
                      {item.subItems && item.subItems.length > 0 && (
                        <div className="ml-4 md:ml-8 space-y-2">
                          {item.subItems.map((subItem) => (
                            <React.Fragment key={subItem.name}>
                              {renderItem(subItem, true)}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CheckList;
